import React from 'react';

const RELOAD_GUARD_KEY = 'fitnessos:lazy-route-reload';
const RELOAD_GUARD_WINDOW_MS = 60000;

export function isRecoverableLazyRouteError(error) {
  const message = `${error?.name || ''} ${error?.message || ''}`;
  return /ChunkLoadError|Loading chunk|dynamically imported module|module script/i.test(message);
}

export default class LazyRouteRecoveryBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (
      isRecoverableLazyRouteError(error)
      && this.props.onRetry
      && this.state.retryCount < (this.props.maxInPlaceRetries || 0)
    ) {
      try {
        const retryPromise = this.props.onRetry();
        void Promise.resolve(retryPromise).catch(() => undefined);
      } finally {
        this.setState((state) => ({
          error: null,
          retryCount: state.retryCount + 1,
        }));
      }
      return;
    }

    if (
      process.env.NODE_ENV !== 'production'
      || !isRecoverableLazyRouteError(error)
      || !this.props.reloadOnChunkError
      || typeof window === 'undefined'
    ) {
      return;
    }

    try {
      const now = Date.now();
      const guardKey = String(
        this.props.reloadGuardKey
        || this.props.resetKey
        || 'route',
      );
      const stored = JSON.parse(window.sessionStorage.getItem(RELOAD_GUARD_KEY) || 'null');
      const recentlyReloaded = (
        stored?.guardKey === guardKey
        && Number.isFinite(stored?.at)
        && now - stored.at < RELOAD_GUARD_WINDOW_MS
      );
      if (recentlyReloaded) return;
      window.sessionStorage.setItem(
        RELOAD_GUARD_KEY,
        JSON.stringify({ guardKey, at: now }),
      );
      window.location.reload();
    } catch {
      /* 저장소나 reload가 차단돼도 기존 로딩 fallback을 유지한다. */
    }
  }

  componentDidUpdate(previousProps) {
    if (
      (this.state.error || this.state.retryCount > 0)
      && previousProps.resetKey !== this.props.resetKey
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ error: null, retryCount: 0 });
    }
  }

  render() {
    if (this.state.error) {
      if (!isRecoverableLazyRouteError(this.state.error)) {
        throw this.state.error;
      }
      return this.props.fallback;
    }
    return this.props.children;
  }
}
